import React, { useEffect, useState } from "react";
import { Card, Row, Col, Statistic, Typography, DatePicker, Select, Button, message } from "antd";
import { 
  DollarOutlined, 
  ShoppingCartOutlined, 
  UserOutlined, 
  ShopOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TeamOutlined,
  FileExclamationOutlined,
  CreditCardOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../api";
import { getUserRole } from "../utils/auth";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalSales: 0,
    totalPurchases: 0,
    totalCustomers: 0, // This now represents total completed invoices
    totalProducts: 0,
    totalExpenses: 0,
    totalEmployees: 0,
    cancelledInvoices: 0,
    totalSuppliers: 0,
    netProfit: 0,
    salesGrowth: 0,
    purchaseGrowth: 0,
    expensesGrowth: 0
  });
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    
    if (role?.role === 'admin') {
      fetchStores();
    } else if (role?.storeId) {
      setSelectedStore(role.storeId);
    }
    
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedStore]);

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Find store name for expenses API
      const selectedStoreData = selectedStore ? stores.find(store => store._id === selectedStore) : null;
      
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        ...(selectedStore && { storeId: selectedStore })
      };

      // Note: Now using dedicated expenses count API that handles store filtering internally

      // Fetch all dashboard data in parallel
      const [
        salesResponse,
        purchasesResponse,
        customersResponse,
        stocksResponse,
        expensesResponse,
        employeesResponse,
        cancelledInvoicesResponse,
        suppliersResponse,
        netProfitResponse
      ] = await Promise.all([
        API.get("/dashboard/sales/amount", { params }),
        API.get("/dashboard/purchase/amount", { params }),
        API.get("/dashboard/customers/count", { params }),
        API.get("/dashboard/stocks", { params }),
        API.get("/dashboard/expenses/count", { params }),
        API.get("/dashboard/employees/count", { params }),
        API.get("/dashboard/cancelled-invoices/count", { params }),
        API.get("/dashboard/suppliers", { params }),
        API.get("/dashboard/net-profit", { params })
      ]);

      // Extract data from responses
      const totalSales = salesResponse.data?.totalAmount || 0;
      const totalPurchases = purchasesResponse.data?.totalAmount || 0;
      const totalCustomers = customersResponse.data?.totalCustomers || 0; // This is now total completed invoices
      const totalProducts = stocksResponse.data?.totalStockLevel || 0;
      const totalExpenses = expensesResponse.data?.totalExpenses || 0;
      const totalEmployees = employeesResponse.data?.totalEmployees || 0;
      const cancelledInvoices = cancelledInvoicesResponse.data?.totalCancelledInvoices || 0;
      const totalSuppliers = suppliersResponse.data?.totalSuppliers || 0;
      
      // Get net profit from sales products (salePrice - purchasePrice)
      const netProfit = netProfitResponse.data?.netProfit || 0;

      setDashboardData({
        totalSales,
        totalPurchases,
        totalCustomers,
        totalProducts,
        totalExpenses,
        totalEmployees,
        cancelledInvoices,
        totalSuppliers,
        netProfit,
        salesGrowth: 0, // TODO: Calculate growth percentage
        purchaseGrowth: 0, // TODO: Calculate growth percentage
        expensesGrowth: 0 // TODO: Calculate growth percentage
      });

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      message.error("Failed to load dashboard data");
      
      // Set fallback data in case of error
      setDashboardData({
        totalSales: 0,
        totalPurchases: 0,
        totalCustomers: 0, // This is now total completed invoices
        totalProducts: 0,
        totalExpenses: 0,
        totalEmployees: 0,
        cancelledInvoices: 0,
        totalSuppliers: 0,
        netProfit: 0,
        salesGrowth: 0,
        purchaseGrowth: 0,
        expensesGrowth: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    console.log('Date range changed:', dates);
    
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      const today = dayjs().endOf('day');
      
      // Ensure end date is not in the future
      if (endDate && endDate.isAfter(today)) {
        message.warning('End date cannot be in the future. Please select today or an earlier date.');
        return;
      }
      
      // Ensure start date is not after end date
      if (startDate && endDate && startDate.isAfter(endDate)) {
        message.warning('Start date cannot be after end date.');
        return;
      }
    }
    
    setDateRange(dates);
  };

  const handleStoreChange = (value) => {
    setSelectedStore(value);
  };

  return (
    <div style={{ padding: "20px", background: "#f5f5f5", minHeight: "100vh" }}>
      <div style={{ marginBottom: "24px" }}>
        <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
          Manager Dashboard
        </Title>
        <p style={{ color: "#666", margin: "8px 0 0 0" }}>
          Welcome back! Here's your store performance overview.
        </p>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Date Range:
            </label>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder={['Start Date', 'End Date']}
              allowClear={false}
              showToday={true}
              disabledDate={(current) => {
                // Disable future dates - end date should not be in the future
                return current && current > dayjs().endOf('day');
              }}
              ranges={{
                'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
                'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
                'This Quarter': [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
                'Last 7 Days': [dayjs().subtract(6, 'day'), dayjs()],
                'Last 30 Days': [dayjs().subtract(29, 'day'), dayjs()],
                'This Year': [dayjs().startOf('year'), dayjs().endOf('year')]
              }}
            />
          </Col>
          {userRole?.role === 'admin' && (
            <Col span={8}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                Store:
              </label>
              <Select
                value={selectedStore}
                onChange={handleStoreChange}
                placeholder="Select Store"
                style={{ width: "100%" }}
                allowClear
              >
                {stores.map((store) => (
                  <Option key={store._id} value={store._id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}
          <Col xs={24} sm={24} lg={8}>
            <div style={{ 
              display: "flex", 
              gap: "8px", 
              marginTop: "24px",
              flexWrap: "wrap",
              justifyContent: "flex-end"
            }}>
              <Button 
                type="primary" 
                onClick={fetchDashboardData}
                loading={loading}
              >
                Refresh Data
              </Button>
              <Button 
                type="primary" 
                onClick={() => navigate("/saleForm")}
              >
                Create New Sale
              </Button>
              <Button 
                onClick={() => navigate("/purchaseForm")}
              >
                Create Purchase
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[4, 4]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Sales"
              value={dashboardData.totalSales}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix="₹"
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              {dashboardData.salesGrowth > 0 ? (
                <span style={{ color: "#52c41a" }}>
                  <ArrowUpOutlined /> +{dashboardData.salesGrowth}% from last period
                </span>
              ) : (
                <span style={{ color: "#ff4d4f" }}>
                  <ArrowDownOutlined /> {dashboardData.salesGrowth}% from last period
                </span>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Purchases"
              value={dashboardData.totalPurchases}
              precision={2}
              prefix={<ShoppingCartOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
              suffix="₹"
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              {dashboardData.purchaseGrowth > 0 ? (
                <span style={{ color: "#52c41a" }}>
                  <ArrowUpOutlined /> +{dashboardData.purchaseGrowth}% from last period
                </span>
              ) : (
                <span style={{ color: "#ff4d4f" }}>
                  <ArrowDownOutlined /> {dashboardData.purchaseGrowth}% from last period
                </span>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Invoices (Completed)"
              value={dashboardData.totalCustomers}
              prefix={<UserOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Completed invoices in this store
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={dashboardData.totalProducts}
              prefix={<ShopOutlined style={{ color: "#fa8c16" }} />}
              valueStyle={{ color: "#fa8c16" }}
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Products in stock for this store
            </div>
          </Card>
        </Col>
      </Row>

      {/* Additional Statistics Cards - Second Row */}
      <Row gutter={[4, 4]} style={{ marginTop: "4px" }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={dashboardData.totalExpenses}
              prefix={<CreditCardOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "#ff4d4f" }}
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Number of expenses for this store
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Employees"
              value={dashboardData.totalEmployees}
              prefix={<TeamOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Current employees in this store
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Cancelled Invoices"
              value={dashboardData.cancelledInvoices}
              prefix={<FileExclamationOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14" }}
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Cancelled invoices this period
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ backgroundColor: "#f6ffed", borderColor: "#52c41a" }}>
            <Statistic
              title="Net Profit"
              value={dashboardData.netProfit}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix="₹"
            />
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              Profit from Sales  
            </div>
          </Card>
        </Col>
        
      </Row>

      {/* Net Profit Card - Third Row */}
      


    </div>
  );
};

export default ManagerDashboard;